import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Col,
  Input,
  Radio,
  Row,
  Spin,
} from 'antd';
import { useTranslation } from 'react-i18next';

// Import Components:
import FormResponder from '../FormResponder';
import FormsTable from '../FormsTable';

// Import Actions:
import { favoriteFormTemplate, getAssignedFormTemplates } from '../state/forms.actions';

// Import Helpers:
import { getIdMap } from '../../helpers/helpers';
import { getManualFormColumns } from '../FormColumns';
import {
  handleFormClick,
  MANUAL_FAVORITE_TAB,
  MANUAL_PROJECT_TAB,
  MANUAL_GLOBAL_TAB,
  getManualFormsTableData,
  getFilters,
} from '../formHelpers';

import {
  formatProjectLabelFromCompanySettings,
} from '../../projects/projectHelpers';
import OnTraccrRadioGroup from '../../common/inputs/OnTraccrRadioGroup';

/** Manual Forms Section */
function ManualForms({
  visible,
  history,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const projects = useSelector((state) => state.projects.projects);
  const assignedFormTemplates = useSelector((state) => state.forms.assignedFormTemplates);
  const latestManualDraftId = useSelector((state) => state.forms.latestManualDraftId);
  const { settings = {} } = useSelector((state) => state.settings.company);

  const [tab, setTab] = useState(MANUAL_FAVORITE_TAB);
  const [assignedForm, setAssignedForm] = useState({});
  const [showDetail, setShowDetail] = useState(false);
  const [selectedIsEdit, setSelectedIsEdit] = useState(false);
  const [selectedIsResubmit, setSelectedIsResubmit] = useState(false);
  const [searchStr, setSearchStr] = useState();
  const [loading, setLoading] = useState(false);

  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);
  const clearFormSelection = useCallback(() => setShowDetail(false), []);

  useEffect(() => {
    setAssignedForm((prevState) => ({
      ...prevState,
      draftId: latestManualDraftId,
    }));
  }, [latestManualDraftId]);

  useEffect(() => {
    if (dispatch) {
      dispatch(getAssignedFormTemplates());
    }
  }, [dispatch]);

  useEffect(() => {
    if (visible) {
      const lastSelectedTab = window.sessionStorage.getItem('manualFormsSectionTab') ?? MANUAL_FAVORITE_TAB;
      if (lastSelectedTab !== MANUAL_FAVORITE_TAB
        && lastSelectedTab !== MANUAL_PROJECT_TAB
        && lastSelectedTab !== MANUAL_GLOBAL_TAB) return;
      setTab(lastSelectedTab);
    }
  }, [visible]);

  // Handlers:
  // -----------------------------------------------------
  const onFormClick = useCallback(async (record) => {
    await handleFormClick({
      record,
      dispatch,
      loading,
      setLoading,
      setAssignedForm,
      setShowDetail,
      setSelectedIsEdit,
      setSelectedIsResubmit,
    });
  }, [dispatch, loading]);

  const onSearch = useCallback((e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    setSearchStr(value);
  }, []);

  const onManualFormTypeChange = useCallback((newTab) => {
    setTab(newTab);
    window.sessionStorage.setItem('manualFormsSectionTab', newTab);
  }, []);

  const onFavoriteClick = useCallback((e, form = {}) => {
    e.stopPropagation();
    const { id, isFavorite } = form;
    if (!id) return;
    dispatch(favoriteFormTemplate(id, isFavorite));
  }, [dispatch]);

  // Prepare data:
  // -----------------------------------------------------
  const tableData = useMemo(() => (
    getManualFormsTableData({
      assignedFormTemplates,
      tab,
      searchStr,
      projectIdMap,
      settings,
    })
  ), [
    tab,
    assignedFormTemplates,
    searchStr,
    projectIdMap,
    settings,
  ]);

  // Prepare filters:
  // -----------------------------------------------------
  const manualProjectFilters = useMemo(() => getFilters({
    data: tableData,
    idMap: projectIdMap,
    key: 'projectId',
    formatter: ({ name, number }) => ({
      name: formatProjectLabelFromCompanySettings({ name, number, settings }),
    }),
  }), [tableData, projectIdMap, settings]);

  // Get columns:
  // -----------------------------------------------------
  const manualColumns = useMemo(() => (
    getManualFormColumns({
      projectIdMap,
      projectFilters: manualProjectFilters,
      tab,
      onFavoriteClick,
      settings,
      t,
    })
  ), [manualProjectFilters, tab, settings, projectIdMap]);

  return (
    <div className="manual-forms">
      <Row
        className="manual-forms-header"
        align="middle"
        justify="space-between"
      >
        <Col>
          <Input.Search
            type="search"
            style={{ width: 250 }}
            className="searchbar"
            placeholder="Search"
            allowClear
            onChange={onSearch}
            value={searchStr}
            data-testid="formsManualSearch"
          />
        </Col>
        <Col>
          <OnTraccrRadioGroup
            value={tab}
            size="medium"
            buttonStyle="outline"
            className="forms-home-tab-toggle"
            onChange={onManualFormTypeChange}
          >
            <Radio.Button value={MANUAL_FAVORITE_TAB}>Favorite</Radio.Button>
            <Radio.Button value={MANUAL_PROJECT_TAB}>{t('Project')}</Radio.Button>
            <Radio.Button value={MANUAL_GLOBAL_TAB}>Global</Radio.Button>
          </OnTraccrRadioGroup>
        </Col>
      </Row>
      <div>
        <FormsTable
          data={tableData}
          columns={manualColumns}
          onClickRow={onFormClick}
        />
        <FormResponder
          visible={showDetail}
          onClose={clearFormSelection}
          history={history}
          assignedForm={assignedForm}
          isOnEditStep={selectedIsEdit}
          isResubmit={selectedIsResubmit}
        />
      </div>
      {loading
          && (
          <Row justify="center" align="middle" className="form-loading-container">
            <Spin size="large" />
          </Row>
          )}
    </div>
  );
}

ManualForms.propTypes = {
  visible: PropTypes.bool.isRequired,
  history: PropTypes.shape({}).isRequired,
};

export default ManualForms;
